import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  QrCode,
  Download,
  Copy,
  ExternalLink,
  Store,
  Printer,
  Sparkles,
  IndianRupee,
  CheckCircle2,
  Share2,
  FileText,
  Wifi,
  Globe,
  Sun,
  Moon,
  Image as ImageIcon,
  Check,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ShopQrSetup() {
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [shopId, setShopId] = useState("");
  const [lanIp, setLanIp] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [colorRate, setColorRate] = useState("20");
  const [bwRate, setBwRate] = useState("10");

  // QR Theme: "light" | "dark"
  const [qrTheme, setQrTheme] = useState("light");
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [downloadingPng, setDownloadingPng] = useState(false);
  const [downloadingSvg, setDownloadingSvg] = useState(false);

  const qrContainerRef = useRef(null);

  // Derive dynamic customer URL based on detected LAN IPv4
  const customerUrl = useMemo(() => {
    if (!shopId) return "";

    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : "";
    const protocol = window.location.protocol;

    if ((hostname === "localhost" || hostname === "127.0.0.1") && lanIp && lanIp !== "127.0.0.1") {
      return `http://${lanIp}${port}/scan-to-print/${encodeURIComponent(shopId)}`;
    }

    return `${protocol}//${hostname}${port}/scan-to-print/${encodeURIComponent(shopId)}`;
  }, [shopId, lanIp]);

  const activeLanHost = useMemo(() => {
    if (lanIp && lanIp !== "127.0.0.1") {
      const port = window.location.port ? `:${window.location.port}` : ":3000";
      return `http://${lanIp}${port}`;
    }
    return window.location.origin;
  }, [lanIp]);

  const shopDisplayName = useMemo(() => {
    return user?.shop_name || shop?.shop_name_english || user?.name || "PrintQ Partner Shop";
  }, [user, shop]);

  const sanitizedFilenamePrefix = useMemo(() => {
    const clean = shopDisplayName.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 30);
    return `PrintQ-QR-${clean || "Shop"}`;
  }, [shopDisplayName]);

  useEffect(() => {
    loadShopAndNetwork();
  }, []);

  const loadShopAndNetwork = async () => {
    setLoading(true);
    try {
      const res = await api.get("/scan-to-print/shop");
      if (res.data?.shop) {
        const s = res.data.shop;
        setShop(s);
        setShopId(s.shop_id || "");
        setColorRate(String(s.color_rate ?? 20));
        setBwRate(String(s.bw_rate ?? 10));
        if (s.lan_ip) {
          setLanIp(s.lan_ip);
        }
      }

      if (!res.data?.shop?.lan_ip) {
        try {
          const netRes = await api.get("/network/lan-info");
          if (netRes.data?.lan_ip) {
            setLanIp(netRes.data.lan_ip);
          }
        } catch (e) {
          console.warn("LAN info notice:", e);
        }
      }
    } catch (err) {
      console.warn("Could not load shop setup:", err);
      toast.error("Could not load shop setup: " + formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRates = async () => {
    setSaving(true);
    try {
      const parsedColor = parseFloat(colorRate);
      const parsedBw = parseFloat(bwRate);

      if (isNaN(parsedColor) || parsedColor < 0) {
        toast.error("Please enter a valid Color rate.");
        setSaving(false);
        return;
      }

      if (isNaN(parsedBw) || parsedBw < 0) {
        toast.error("Please enter a valid B&W rate.");
        setSaving(false);
        return;
      }

      const payload = {
        color_rate: parsedColor,
        bw_rate: parsedBw,
      };

      const res = await api.put("/scan-to-print/shop", payload);
      if (res.data?.shop) {
        setShop(res.data.shop);
        if (res.data.shop.lan_ip) setLanIp(res.data.shop.lan_ip);
        toast.success("Printing rates updated successfully!");
      }
    } catch (err) {
      console.error("Save rates error:", err);
      toast.error("Failed to update rates: " + formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = () => {
    if (!customerUrl) return;
    navigator.clipboard.writeText(customerUrl);
    setCopiedUrl(true);
    toast.success("Customer QR URL copied to clipboard!");
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  // ----------------------------------------------------
  // 1. DOWNLOAD PNG (High-Res Branded Counter Card)
  // ----------------------------------------------------
  const handleDownloadPng = async () => {
    if (!customerUrl) return;
    setDownloadingPng(true);
    try {
      const isDark = qrTheme === "dark";
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const width = 1200;
      const height = 1500;
      canvas.width = width;
      canvas.height = height;

      // 1. Background
      ctx.fillStyle = isDark ? "#09090b" : "#ffffff";
      ctx.fillRect(0, 0, width, height);

      // Outer Border
      ctx.lineWidth = 12;
      ctx.strokeStyle = isDark ? "#f59e0b" : "#d97706";
      ctx.strokeRect(30, 30, width - 60, height - 60);

      // 2. Header
      ctx.textAlign = "center";
      ctx.fillStyle = isDark ? "#f59e0b" : "#d97706";
      ctx.font = "bold 44px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("PRINTQ", width / 2, 130);

      ctx.fillStyle = isDark ? "#a1a1aa" : "#71717a";
      ctx.font = "bold 20px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("SCAN. ENHANCE. PRINT.", width / 2, 175);

      // Title Box
      ctx.fillStyle = isDark ? "#ffffff" : "#18181b";
      ctx.font = "900 68px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("SCAN TO PRINT", width / 2, 270);

      ctx.fillStyle = isDark ? "#d4d4d8" : "#52525b";
      ctx.font = "500 28px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("Send documents directly from your mobile phone", width / 2, 320);

      // 3. Draw QR SVG onto Canvas
      const svgElement = qrContainerRef.current?.querySelector("svg");
      if (!svgElement) throw new Error("QR code element not found");

      const svgString = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const URLObj = window.URL || window.webkitURL || window;
      const blobURL = URLObj.createObjectURL(svgBlob);

      const qrImg = new Image();
      await new Promise((resolve, reject) => {
        qrImg.onload = resolve;
        qrImg.onerror = reject;
        qrImg.src = blobURL;
      });

      // QR Box Background
      const qrBoxSize = 680;
      const qrX = (width - qrBoxSize) / 2;
      const qrY = 370;

      ctx.fillStyle = isDark ? "#18181b" : "#f4f4f5";
      ctx.beginPath();
      ctx.roundRect(qrX, qrY, qrBoxSize, qrBoxSize, 40);
      ctx.fill();
      ctx.strokeStyle = isDark ? "#27272a" : "#e4e4e7";
      ctx.lineWidth = 4;
      ctx.stroke();

      // QR Image (centered inside box with quiet zone)
      const qrDrawSize = 560;
      const qrDrawX = (width - qrDrawSize) / 2;
      const qrDrawY = qrY + (qrBoxSize - qrDrawSize) / 2;
      ctx.drawImage(qrImg, qrDrawX, qrDrawY, qrDrawSize, qrDrawSize);
      URLObj.revokeObjectURL(blobURL);

      // 4. Shop Name Highlight
      ctx.fillStyle = isDark ? "#f59e0b" : "#d97706";
      ctx.font = "bold 26px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("SHOP / SERVICE CENTER", width / 2, 1130);

      ctx.fillStyle = isDark ? "#ffffff" : "#18181b";
      ctx.font = "800 48px 'Segoe UI', Arial, sans-serif";
      ctx.fillText(shopDisplayName, width / 2, 1195);

      // Rates pill
      ctx.fillStyle = isDark ? "#27272a" : "#e4e4e7";
      ctx.beginPath();
      ctx.roundRect(width / 2 - 320, 1250, 640, 65, 32);
      ctx.fill();

      ctx.fillStyle = isDark ? "#fbbf24" : "#b45309";
      ctx.font = "bold 28px 'Segoe UI', Arial, sans-serif";
      ctx.fillText(`B&W: ₹${bwRate}/page   •   Color: ₹${colorRate}/page`, width / 2, 1294);

      // 5. Footer Attribution
      ctx.fillStyle = isDark ? "#71717a" : "#a1a1aa";
      ctx.font = "600 20px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("POWERED BY JSM DIGITAL SOLUTIONS", width / 2, 1420);

      // Download trigger
      canvas.toBlob((blob) => {
        if (!blob) return;
        const link = document.createElement("a");
        link.download = `${sanitizedFilenamePrefix}-${qrTheme === "dark" ? "Dark" : "Light"}.png`;
        link.href = URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("High-resolution PNG QR downloaded successfully!");
      }, "image/png");
    } catch (err) {
      console.error("PNG Download Error:", err);
      toast.error("Failed to generate PNG download.");
    } finally {
      setDownloadingPng(false);
    }
  };

  // ----------------------------------------------------
  // 2. DOWNLOAD SVG (Vector Quality)
  // ----------------------------------------------------
  const handleDownloadSvg = () => {
    if (!customerUrl) return;
    setDownloadingSvg(true);
    try {
      const isDark = qrTheme === "dark";
      const svgElement = qrContainerRef.current?.querySelector("svg");
      if (!svgElement) throw new Error("QR code element not found");

      const qrInnerSvg = new XMLSerializer().serializeToString(svgElement);

      const fullSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 1000" width="800" height="1000">
  <style>
    .brand { font-family: 'Segoe UI', Arial, sans-serif; font-size: 32px; font-weight: 800; fill: ${isDark ? "#f59e0b" : "#d97706"}; text-anchor: middle; }
    .tagline { font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; font-weight: 700; letter-spacing: 2px; fill: ${isDark ? "#a1a1aa" : "#71717a"}; text-anchor: middle; }
    .title { font-family: 'Segoe UI', Arial, sans-serif; font-size: 46px; font-weight: 900; fill: ${isDark ? "#ffffff" : "#18181b"}; text-anchor: middle; }
    .subtitle { font-family: 'Segoe UI', Arial, sans-serif; font-size: 18px; fill: ${isDark ? "#d4d4d8" : "#52525b"}; text-anchor: middle; }
    .shop-title { font-family: 'Segoe UI', Arial, sans-serif; font-size: 16px; font-weight: 700; fill: ${isDark ? "#f59e0b" : "#d97706"}; text-anchor: middle; }
    .shop-name { font-family: 'Segoe UI', Arial, sans-serif; font-size: 32px; font-weight: 800; fill: ${isDark ? "#ffffff" : "#18181b"}; text-anchor: middle; }
    .rates { font-family: 'Segoe UI', Arial, sans-serif; font-size: 18px; font-weight: 700; fill: ${isDark ? "#fbbf24" : "#b45309"}; text-anchor: middle; }
    .footer { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 1.5px; fill: ${isDark ? "#71717a" : "#a1a1aa"}; text-anchor: middle; }
  </style>
  <rect width="100%" height="100%" fill="${isDark ? "#09090b" : "#ffffff"}"/>
  <rect x="20" y="20" width="760" height="960" rx="24" fill="none" stroke="${isDark ? "#f59e0b" : "#d97706"}" stroke-width="8"/>
  
  <text x="400" y="90" class="brand">PRINTQ</text>
  <text x="400" y="118" class="tagline">SCAN. ENHANCE. PRINT.</text>
  
  <text x="400" y="180" class="title">SCAN TO PRINT</text>
  <text x="400" y="215" class="subtitle">Send documents directly from your mobile</text>
  
  <!-- QR Box -->
  <rect x="180" y="250" width="440" height="440" rx="24" fill="${isDark ? "#18181b" : "#f4f4f5"}" stroke="${isDark ? "#27272a" : "#e4e4e7"}" stroke-width="3"/>
  <g transform="translate(210, 280) scale(1.47)">
    ${qrInnerSvg.replace(/^<svg[^>]*>/, "").replace(/<\/svg>$/, "")}
  </g>
  
  <text x="400" y="745" class="shop-title">SHOP / SERVICE CENTER</text>
  <text x="400" y="790" class="shop-name">${shopDisplayName}</text>
  
  <rect x="200" y="825" width="400" height="45" rx="22" fill="${isDark ? "#27272a" : "#e4e4e7"}"/>
  <text x="400" y="855" class="rates">B&amp;W: ₹${bwRate}/page   •   Color: ₹${colorRate}/page</text>
  
  <text x="400" y="940" class="footer">POWERED BY JSM DIGITAL SOLUTIONS</text>
</svg>`;

      const blob = new Blob([fullSvg], { type: "image/svg+xml;charset=utf-8" });
      const link = document.createElement("a");
      link.download = `${sanitizedFilenamePrefix}-${qrTheme === "dark" ? "Dark" : "Light"}.svg`;
      link.href = URL.createObjectURL(blob);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Vector SVG QR downloaded successfully!");
    } catch (err) {
      console.error("SVG Download Error:", err);
      toast.error("Failed to generate SVG download.");
    } finally {
      setDownloadingSvg(false);
    }
  };

  // ----------------------------------------------------
  // 3. PRINT QR COUNTER CARD
  // ----------------------------------------------------
  const handlePrintQr = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow popups to print counter QR.");
      return;
    }

    const isDark = qrTheme === "dark";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>PrintQ — Customer QR Card</title>
          <style>
            @page { size: auto; margin: 15mm; }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              background: ${isDark ? "#09090b" : "#ffffff"};
              color: ${isDark ? "#ffffff" : "#09090b"};
              margin: 0;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              text-align: center;
            }
            .card {
              max-width: 480px;
              border: 4px solid ${isDark ? "#f59e0b" : "#d97706"};
              border-radius: 24px;
              padding: 30px;
              background: ${isDark ? "#18181b" : "#ffffff"};
            }
            .brand { font-size: 28px; font-weight: 900; color: ${isDark ? "#f59e0b" : "#d97706"}; margin: 0; }
            .tagline { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; color: ${isDark ? "#a1a1aa" : "#71717a"}; margin-top: 2px; }
            .title { font-size: 32px; font-weight: 900; margin-top: 15px; margin-bottom: 2px; }
            .subtitle { font-size: 13px; color: ${isDark ? "#a1a1aa" : "#52525b"}; margin-bottom: 20px; }
            .qr-box {
              padding: 20px;
              background: ${isDark ? "#27272a" : "#f4f4f5"};
              border-radius: 20px;
              display: inline-block;
              margin-bottom: 20px;
            }
            .shop-name { font-size: 22px; font-weight: 800; margin: 10px 0 5px 0; }
            .rates { font-size: 14px; font-weight: 700; color: ${isDark ? "#fbbf24" : "#b45309"}; margin-top: 10px; }
            .footer { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: ${isDark ? "#71717a" : "#a1a1aa"}; margin-top: 20px; }
          </style>
          <script src="https://unpkg.com/qrcode-generator@1.4.4/qrcode.js"></script>
        </head>
        <body>
          <div class="card">
            <h1 class="brand">PRINTQ</h1>
            <div class="tagline">Scan. Enhance. Print.</div>
            <div class="title">SCAN TO PRINT</div>
            <div class="subtitle">Send documents directly from your mobile</div>
            <div class="qr-box">
              <div id="qr-target"></div>
            </div>
            <div class="shop-name">${shopDisplayName}</div>
            <div class="rates">B&W: ₹${bwRate}/page • Color: ₹${colorRate}/page</div>
            <div class="footer">Powered by JSM DIGITAL SOLUTIONS</div>
          </div>
          <script>
            var qr = qrcode(0, 'M');
            qr.addData(${JSON.stringify(customerUrl)});
            qr.make();
            document.getElementById('qr-target').innerHTML = qr.createSvgTag(6, 0);
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ----------------------------------------------------
  // 4. GENERATE PRINTABLE A4 POSTER (210mm × 297mm)
  // ----------------------------------------------------
  const handlePrintPoster = () => {
    const posterWindow = window.open("", "_blank");
    if (!posterWindow) {
      toast.error("Please allow popups to print counter poster.");
      return;
    }

    const isDark = qrTheme === "dark";

    posterWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>PrintQ — A4 Scan to Print Poster</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              color: ${isDark ? "#ffffff" : "#111827"};
              background: ${isDark ? "#09090b" : "#ffffff"};
              margin: 0;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              box-sizing: border-box;
              min-height: 100vh;
            }
            .poster-container {
              width: 100%;
              max-width: 720px;
              border: 5px solid ${isDark ? "#f59e0b" : "#d97706"};
              border-radius: 28px;
              padding: 25px;
              box-sizing: border-box;
              background: ${isDark ? "#18181b" : "#ffffff"};
            }
            .header {
              border-bottom: 2px solid ${isDark ? "#27272a" : "#e5e7eb"};
              padding-bottom: 12px;
              width: 100%;
              margin-bottom: 15px;
            }
            .brand-logo {
              font-size: 44px;
              font-weight: 900;
              color: ${isDark ? "#ffffff" : "#18181b"};
              margin: 0;
              letter-spacing: -1px;
            }
            .brand-q { color: ${isDark ? "#f59e0b" : "#d97706"}; }
            .tagline {
              font-size: 13px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 3px;
              color: ${isDark ? "#a1a1aa" : "#4b5563"};
              margin-top: 4px;
            }
            .main-title {
              font-size: 38px;
              font-weight: 900;
              color: ${isDark ? "#ffffff" : "#18181b"};
              margin: 10px 0 2px 0;
            }
            .sub-title {
              font-size: 15px;
              color: ${isDark ? "#d4d4d8" : "#4b5563"};
              margin-bottom: 15px;
            }
            .qr-wrapper {
              display: inline-block;
              padding: 22px;
              background: ${isDark ? "#27272a" : "#f4f4f5"};
              border: 3px solid ${isDark ? "#f59e0b" : "#18181b"};
              border-radius: 24px;
              margin-bottom: 15px;
            }
            .badge {
              display: inline-block;
              padding: 5px 14px;
              border-radius: 20px;
              font-size: 11px;
              font-weight: 800;
              letter-spacing: 1px;
              text-transform: uppercase;
              background: ${isDark ? "#f59e0b15" : "#d9770615"};
              color: ${isDark ? "#fbbf24" : "#d97706"};
              border: 1px solid ${isDark ? "#f59e0b30" : "#d9770630"};
              margin-bottom: 15px;
            }
            .shop-title {
              font-size: 13px;
              font-weight: 700;
              color: ${isDark ? "#f59e0b" : "#d97706"};
              text-transform: uppercase;
              letter-spacing: 1.5px;
              margin: 0;
            }
            .shop-name {
              font-size: 26px;
              font-weight: 800;
              color: ${isDark ? "#ffffff" : "#18181b"};
              margin: 4px 0 15px 0;
            }
            .steps-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
              width: 100%;
              margin: 15px 0;
              text-align: left;
            }
            .step-card {
              padding: 12px;
              border-radius: 14px;
              background: ${isDark ? "#27272a" : "#f9fafb"};
              border: 1px solid ${isDark ? "#3f3f46" : "#e5e7eb"};
            }
            .step-header {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 13px;
              font-weight: 800;
              color: ${isDark ? "#ffffff" : "#1f2937"};
            }
            .step-num {
              width: 22px;
              height: 22px;
              border-radius: 50%;
              background: ${isDark ? "#f59e0b" : "#18181b"};
              color: ${isDark ? "#09090b" : "#ffffff"};
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 11px;
              font-weight: 900;
            }
            .step-desc {
              font-size: 11px;
              color: ${isDark ? "#a1a1aa" : "#6b7280"};
              margin-top: 4px;
              margin-left: 30px;
            }
            .rates-box {
              display: flex;
              justify-content: center;
              gap: 25px;
              margin: 15px 0;
              padding: 12px 30px;
              border-radius: 16px;
              background: ${isDark ? "#27272a" : "#fffbeb"};
              border: 1px solid ${isDark ? "#3f3f46" : "#fef3c7"};
            }
            .rate-item {
              font-size: 16px;
              font-weight: 800;
              color: ${isDark ? "#fbbf24" : "#92400e"};
            }
            .footer-tag {
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 2px;
              color: ${isDark ? "#71717a" : "#9ca3af"};
              margin-top: 15px;
            }
          </style>
          <script src="https://unpkg.com/qrcode-generator@1.4.4/qrcode.js"></script>
        </head>
        <body>
          <div class="poster-container">
            <div class="header">
              <h1 class="brand-logo">PRINT<span class="brand-q">Q</span></h1>
              <div class="tagline">Scan. Enhance. Print.</div>
            </div>

            <div class="main-title">SCAN TO PRINT</div>
            <div class="sub-title">Upload your documents directly from your mobile</div>

            <div class="badge">No App Installation Required</div>

            <div class="qr-wrapper">
              <div id="qr-target"></div>
            </div>

            <div>
              <p class="shop-title">Service Center</p>
              <h2 class="shop-name">${shopDisplayName}</h2>
            </div>

            <div class="steps-grid">
              <div class="step-card">
                <div class="step-header">
                  <div class="step-num">1</div>
                  <span>Scan QR Code</span>
                </div>
                <div class="step-desc">Open link with camera</div>
              </div>
              <div class="step-card">
                <div class="step-header">
                  <div class="step-num">2</div>
                  <span>Upload Files</span>
                </div>
                <div class="step-desc">PDF, Photos, ID Cards</div>
              </div>
              <div class="step-card">
                <div class="step-header">
                  <div class="step-num">3</div>
                  <span>Collect Print</span>
                </div>
                <div class="step-desc">Ready at counter</div>
              </div>
            </div>

            <div class="rates-box">
              <div class="rate-item">B&amp;W Print: ₹${bwRate}/page</div>
              <div style="color: ${isDark ? "#71717a" : "#d97706"};">•</div>
              <div class="rate-item">Color Print: ₹${colorRate}/page</div>
            </div>

            <div class="footer-tag">PrintQ • Powered by JSM DIGITAL SOLUTIONS</div>
          </div>

          <script>
            var qr = qrcode(0, 'H');
            qr.addData(${JSON.stringify(customerUrl)});
            qr.make();
            document.getElementById('qr-target').innerHTML = qr.createSvgTag(8, 0);
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    posterWindow.document.close();
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto flex flex-col gap-6 selection:bg-amber-500/30">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-zinc-900/80 border border-zinc-800 rounded-3xl p-6 shadow-xl backdrop-blur-md">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold">
              <QrCode className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-white tracking-tight flex items-center gap-1.5">
                <span>Customer QR & Printing Rates</span>
              </h1>
              <p className="text-xs text-zinc-400 mt-0.5">
                Customer mobile upload QR code generator, download suite, and counter poster printing.
              </p>
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={loadShopAndNetwork}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 text-xs font-semibold text-zinc-300 hover:text-white transition-all"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: QR Card & Download Suite (7 Cols) */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          {/* Main Counter QR Card */}
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 shadow-xl flex flex-col items-center text-center gap-6">
            {/* Top Bar with Theme Selector */}
            <div className="w-full flex items-center justify-between border-b border-zinc-800/80 pb-4">
              <div className="text-left">
                <span className="text-xs font-bold text-white uppercase tracking-wider block">Customer Mobile Portal</span>
                <span className="text-[11px] text-zinc-400">Direct WiFi & Counter Upload</span>
              </div>

              {/* Theme Toggle Cards */}
              <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-xl p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setQrTheme("light")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-all ${
                    qrTheme === "light"
                      ? "bg-white text-zinc-950 shadow-sm"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Sun className="w-3.5 h-3.5 text-amber-500" />
                  <span>LIGHT</span>
                </button>
                <button
                  type="button"
                  onClick={() => setQrTheme("dark")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-all ${
                    qrTheme === "dark"
                      ? "bg-amber-500 text-zinc-950 shadow-sm"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Moon className="w-3.5 h-3.5 text-zinc-950" />
                  <span>DARK</span>
                </button>
              </div>
            </div>

            {/* Live QR Box (Theme-Reactive) */}
            <div
              ref={qrContainerRef}
              className={`p-6 rounded-3xl border-2 transition-all duration-300 shadow-2xl flex flex-col items-center ${
                qrTheme === "light"
                  ? "bg-white border-zinc-300 text-zinc-900"
                  : "bg-zinc-950 border-amber-500/40 text-white"
              }`}
            >
              <div className="text-center mb-3">
                <span className="text-xs font-black tracking-tight uppercase" style={{ color: qrTheme === "light" ? "#d97706" : "#f59e0b" }}>
                  PRINTQ
                </span>
                <h3 className="text-lg font-black tracking-tight" style={{ color: qrTheme === "light" ? "#18181b" : "#ffffff" }}>
                  SCAN TO PRINT
                </h3>
              </div>

              {customerUrl ? (
                <div className="p-3 bg-white rounded-2xl shadow-inner inline-block">
                  <QRCodeSVG
                    value={customerUrl}
                    size={220}
                    level="H"
                    includeMargin={true}
                    fgColor="#09090b"
                    bgColor="#ffffff"
                  />
                </div>
              ) : (
                <div className="w-[220px] h-[220px] rounded-2xl bg-zinc-800/40 flex items-center justify-center text-xs text-zinc-500">
                  Generating QR...
                </div>
              )}

              <div className="mt-4 text-center">
                <p className="text-xs font-bold" style={{ color: qrTheme === "light" ? "#18181b" : "#ffffff" }}>
                  {shopDisplayName}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: qrTheme === "light" ? "#71717a" : "#a1a1aa" }}>
                  B&W: ₹{bwRate}/page • Color: ₹{colorRate}/page
                </p>
              </div>
            </div>

            {/* Action Buttons Suite */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 w-full">
              <button
                type="button"
                onClick={handleDownloadPng}
                disabled={downloadingPng || !customerUrl}
                className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 text-xs font-bold text-white transition-all active:scale-95 cursor-pointer shadow-md disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5 text-amber-400" />
                <span>{downloadingPng ? "Saving..." : "PNG QR"}</span>
              </button>

              <button
                type="button"
                onClick={handleDownloadSvg}
                disabled={downloadingSvg || !customerUrl}
                className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 text-xs font-bold text-white transition-all active:scale-95 cursor-pointer shadow-md disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5 text-cyan-400" />
                <span>SVG QR</span>
              </button>

              <button
                type="button"
                onClick={handlePrintQr}
                disabled={!customerUrl}
                className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 text-xs font-bold text-white transition-all active:scale-95 cursor-pointer shadow-md"
              >
                <Printer className="w-3.5 h-3.5 text-emerald-400" />
                <span>Print QR</span>
              </button>

              <button
                type="button"
                onClick={handlePrintPoster}
                disabled={!customerUrl}
                className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 active:scale-95 text-zinc-950 text-xs font-black shadow-lg shadow-amber-500/20 transition-all cursor-pointer"
              >
                <FileText className="w-3.5 h-3.5" />
                <span>A4 Poster</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Rates & LAN Network Details (5 Cols) */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          {/* Printing Rates Form Card */}
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 shadow-xl flex flex-col gap-4">
            <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
              <IndianRupee className="w-5 h-5 text-amber-400" />
              <h2 className="text-sm font-bold text-white">Customer Printing Rates</h2>
            </div>

            <p className="text-xs text-zinc-400">
              Set the per-page rates shown to customers when they upload documents.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-zinc-300">B&W Print Rate</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-bold">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={bwRate}
                    onChange={(e) => setBwRate(e.target.value)}
                    className="w-full pl-8 pr-3 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white font-mono text-sm outline-none focus:border-amber-500/60"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-zinc-300">Color Print Rate</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-bold">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={colorRate}
                    onChange={(e) => setColorRate(e.target.value)}
                    className="w-full pl-8 pr-3 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white font-mono text-sm outline-none focus:border-amber-500/60"
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              disabled={saving}
              onClick={handleSaveRates}
              className="mt-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-110 active:scale-95 text-zinc-950 font-bold text-xs shadow-md shadow-amber-500/20 transition-all cursor-pointer disabled:opacity-50"
            >
              {saving ? "Saving Rates..." : "Save Printing Rates"}
            </button>
          </div>

          {/* Network & Customer URL Details */}
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 shadow-xl flex flex-col gap-4">
            <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
              <Wifi className="w-5 h-5 text-emerald-400" />
              <h2 className="text-sm font-bold text-white">LAN WiFi Connectivity</h2>
            </div>

            <div className="flex flex-col gap-3 text-xs">
              <div className="flex justify-between items-center bg-zinc-950 p-3 rounded-xl border border-zinc-800">
                <span className="text-zinc-400">PC LAN IP:</span>
                <span className="font-mono text-emerald-400 font-bold">{lanIp || "127.0.0.1"}</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-zinc-400 font-semibold">Active Customer URL:</span>
                <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 p-2.5 rounded-xl">
                  <span className="font-mono text-[11px] text-zinc-200 truncate mr-2">
                    {customerUrl || "Loading URL..."}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={handleCopyUrl}
                      className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                      title="Copy URL"
                    >
                      {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <a
                      href={customerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                      title="Open in Browser"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-zinc-500 leading-relaxed">
                Customers connected to your shop's Wi-Fi network can scan the QR code to upload PDFs, photos, and ID cards directly to your print queue.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
